import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";
import { trackEvent } from "../../lib/analytics";

const LOGO = "/brand-logo.png";

const NAV_LINKS = [
  { label: "Features", to: "/features" },
  { label: "Industries", to: "/industries" },
  { label: "Solutions", to: "/solutions" },
  { label: "Resources", to: "/resources" },
  { label: "Pricing", to: "/pricing" },
  { label: "Contact", to: "/contact" },
];

/**
 * Shared nav + footer shell for all public marketing pages (features,
 * industries, solutions, resources, compare). Keeps a consistent brand shell
 * and dense internal-linking footer across the whole marketing site without
 * touching the existing homepage (Landing.jsx) layout/tests.
 */
export function MarketingLayout({ breadcrumb, children }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-950 text-white antialiased">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-slate-950/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="marketing-logo-link">
            <div className="h-9 w-9 rounded-lg bg-white flex items-center justify-center overflow-hidden">
              <img src={LOGO} alt="ServiceGuru Pro" className="h-full w-full object-contain" />
            </div>
            <span className="font-bold text-white text-[15px]">ServiceGuru Pro</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`marketing-nav-${l.label.toLowerCase()}`}
                className="px-3.5 py-2 rounded-lg text-[13.5px] text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-2">
            <Link to="/login" data-testid="marketing-nav-login" className="px-3.5 py-2 rounded-lg text-[13.5px] text-slate-300 hover:text-white">
              Log in
            </Link>
            <Link
              to="/signup"
              data-testid="marketing-nav-cta"
              onClick={() => trackEvent("cta_click", { cta_name: "nav_start_trial", page_path: window.location.pathname })}
              className="px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-[13.5px] font-semibold transition-colors"
            >
              Start free trial
            </Link>
          </div>
          <button className="md:hidden p-2 text-white" onClick={() => setMenuOpen((v) => !v)} data-testid="marketing-mobile-menu-btn">
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-white/10 px-5 py-4 space-y-1" data-testid="marketing-mobile-menu">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setMenuOpen(false)} className="block px-2 py-2.5 text-[14px] text-slate-300 hover:text-white">
                {l.label}
              </Link>
            ))}
            <Link to="/login" onClick={() => setMenuOpen(false)} className="block px-2 py-2.5 text-[14px] text-slate-300 hover:text-white">
              Log in
            </Link>
            <button
              onClick={() => navigate("/signup")}
              data-testid="marketing-mobile-cta"
              className="w-full mt-2 px-4 py-2.5 rounded-full bg-blue-600 text-white text-[14px] font-semibold"
            >
              Start free trial
            </button>
          </div>
        )}
      </header>

      {breadcrumb && breadcrumb.length > 0 && (
        <div className="max-w-6xl mx-auto px-5 lg:px-8 pt-5 flex items-center gap-1.5 text-[12.5px] text-slate-500" data-testid="marketing-breadcrumb">
          {breadcrumb.map((b, i) => (
            <span key={b.path} className="flex items-center gap-1.5">
              {i > 0 && <ChevronRight className="h-3 w-3" />}
              {i === breadcrumb.length - 1 ? (
                <span className="text-slate-300">{b.name}</span>
              ) : (
                <Link to={b.path} className="hover:text-white transition-colors">
                  {b.name}
                </Link>
              )}
            </span>
          ))}
        </div>
      )}

      <main>{children}</main>

      <footer className="bg-[#0B1220] text-slate-400 border-t border-white/10 mt-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-lg bg-white flex items-center justify-center overflow-hidden">
                <img src={LOGO} alt="ServiceGuru Pro" className="h-full w-full object-contain" />
              </div>
              <span className="text-white font-bold text-[15px] tracking-tight">ServiceGuru Pro</span>
            </div>
            <p className="mt-3 text-[13px] max-w-[230px] leading-relaxed">
              The AI-powered home service CRM for HVAC, plumbing, electrical, pool, landscaping, roofing and general contractors.
            </p>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Features</div>
            <ul className="mt-3 space-y-2.5 text-[13.5px]">
              <li><Link to="/features/dispatching" className="hover:text-white transition-colors">Dispatching</Link></li>
              <li><Link to="/features/estimates" className="hover:text-white transition-colors">Estimates</Link></li>
              <li><Link to="/features/invoicing" className="hover:text-white transition-colors">Invoicing</Link></li>
              <li><Link to="/features/ai-voice-agents" className="hover:text-white transition-colors">AI Voice Agents</Link></li>
              <li><Link to="/features" className="hover:text-white transition-colors">All features</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Industries</div>
            <ul className="mt-3 space-y-2.5 text-[13.5px]">
              <li><Link to="/industries/hvac" className="hover:text-white transition-colors">HVAC</Link></li>
              <li><Link to="/industries/plumbing" className="hover:text-white transition-colors">Plumbing</Link></li>
              <li><Link to="/industries/electrical" className="hover:text-white transition-colors">Electrical</Link></li>
              <li><Link to="/industries" className="hover:text-white transition-colors">All industries</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Company</div>
            <ul className="mt-3 space-y-2.5 text-[13.5px]">
              <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
              <li><Link to="/resources" className="hover:text-white transition-colors">Resources</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
              <li><Link to="/signup" className="hover:text-white transition-colors">Start free trial</Link></li>
              <li><Link to="/become-an-affiliate" className="hover:text-white transition-colors">Become an Affiliate</Link></li>
              <li><Link to="/login" className="hover:text-white transition-colors">Log in</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-6xl mx-auto px-5 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-[12.5px] text-slate-500">
            <span>© {new Date().getFullYear()} ServiceGuru Pro. All rights reserved.</span>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
